import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import React, { FC, useContext, useEffect, useState } from 'react';
import { useRedirectOnAuth } from '../../aws/AuthHooks';
import { AuthState } from '../../aws/AuthProvider';
import { DataContext } from '../../aws/DataProvider';
import { CustomDataGrid } from '../../components/data-grid/CustomDataGrid';
import { StatusDot } from '../../components/status-dot/StatusDot';
import { TypeBadge, TypeBadgeVariant } from '../../components/type-badge/TypeBadge';
import { CustomModal } from '../../components/modal/CustomModal';
import { RequestModal } from '../../components/modals/request-modal/RequestModal';
import { BefundungModal } from '../../components/modals/befundung-modal/BefundungModal';
import { getRequestTableRow, RequestListRow } from '../../helpers/requests';
import { useStyles } from '../../theme/styles.helpers';
import { ROUTE } from '../routes';
import { OrderRequestsStyles } from './OrderRequests.Styles';

type TypeFilter = 'all' | 'rezept' | 'befundung';

const TYPE_FILTER_LABELS: Record<TypeFilter, string> = {
  all: 'Alle Typen',
  rezept: '💊 Rezept',
  befundung: '🧪 Befundung',
};

const getTypeBadgeVariant = (isBefundung: boolean): TypeBadgeVariant => (isBefundung ? 'befundung' : 'rezept');

const formatDate = (date: Date): string => {
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
};

const OrderRequests: FC = () => {
  useRedirectOnAuth([AuthState.UNAUTHORIZED], ROUTE.LOGIN);

  const {
    ordersState: [requests],
    usersState: [users],
  } = useContext(DataContext);

  const styles = useStyles(OrderRequestsStyles, {});

  const [allRows, setAllRows] = useState<RequestListRow[]>([]);
  const [rows, setRows] = useState<RequestListRow[]>([]);
  const [freeTextSearch, setFreeTextSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isBefundungModalOpen, setIsBefundungModalOpen] = useState(false);
  const [requestId, setRequestId] = useState<string | undefined>(undefined);
  const [isFollowup, setIsFollowup] = useState(false);

  useEffect(() => {
    const mapped = requests.map((r) => getRequestTableRow(r, users));
    setAllRows(mapped);
  }, [requests, users]);

  useEffect(() => {
    let filtered = allRows;

    if (typeFilter !== 'all') {
      const wantsBefundung = typeFilter === 'befundung';
      filtered = filtered.filter((row) => row.isBefundung === wantsBefundung);
    }

    if (freeTextSearch.trim()) {
      const query = freeTextSearch.toLowerCase();
      filtered = filtered.filter((row) =>
        Object.values(row).some((value) => typeof value === 'string' && value.toLowerCase().includes(query))
      );
    }

    setRows(filtered);
  }, [allRows, typeFilter, freeTextSearch]);

  const handleRowClick = (params: GridRowParams) => {
    const row = params.row as RequestListRow;
    setRequestId(row.orderId);
    setIsFollowup(row.isFollowup);
    if (row.isBefundung) {
      setIsBefundungModalOpen(true);
    } else {
      setIsEditModalOpen(true);
    }
  };

  const handleTypeFilterChange = (event: SelectChangeEvent<TypeFilter>) => {
    setTypeFilter(event.target.value as TypeFilter);
  };

  const columns: GridColDef[] = [
    {
      field: 'orderId',
      headerName: 'ID',
      flex: 0.7,
      renderCell: (params: GridRenderCellParams) => <Box sx={styles.idCell}>{String(params.value).slice(0, 8)}…</Box>,
    },
    {
      field: 'orderDate',
      headerName: 'Datum',
      flex: 0.6,
      type: 'date',
      renderCell: (params: GridRenderCellParams) => <Box sx={styles.dateCell}>{formatDate(params.value as Date)}</Box>,
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => <Box sx={styles.nameCell}>{params.value}</Box>,
    },
    {
      field: 'product',
      headerName: 'Produkt',
      flex: 2,
      renderCell: (params: GridRenderCellParams) => <Box sx={styles.productCell}>{params.value}</Box>,
    },
    {
      field: 'isBefundung',
      headerName: 'Typ',
      flex: 0.7,
      renderCell: (params: GridRenderCellParams) => (
        <TypeBadge variant={getTypeBadgeVariant(params.value as boolean)} />
      ),
    },
    {
      field: 'requestState',
      headerName: '',
      flex: 0.2,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => <StatusDot state={params.value as string} />,
      sortComparator: (a, b) => {
        if (a === 'IDLE' && b === 'IDLE') return 0;
        if (a === 'IDLE' && b === 'ALLOWED') return -1;
        if (a === 'IDLE' && b === 'DENIED') return -1;
        if (a === 'ALLOWED' && b === 'IDLE') return 1;
        if (a === 'ALLOWED' && b === 'ALLOWED') return 0;
        if (a === 'ALLOWED' && b === 'DENIED') return -1;
        if (a === 'DENIED' && b === 'IDLE') return 1;
        if (a === 'DENIED' && b === 'ALLOWED') return 1;
        if (a === 'DENIED' && b === 'DENIED') return 0;

        return 0;
      },
    },
  ];

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Typography variant="h3">Anfragen</Typography>

        <Box sx={styles.controls}>
          <OutlinedInput
            sx={styles.searchInput}
            placeholder="Suchen..."
            value={freeTextSearch}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFreeTextSearch(e.target.value)}
          />

          <Select<TypeFilter>
            sx={styles.typeSelect}
            value={typeFilter}
            onChange={handleTypeFilterChange}
            input={<OutlinedInput />}
            renderValue={(value) => TYPE_FILTER_LABELS[value]}
          >
            <MenuItem value="all">Alle Typen</MenuItem>
            <MenuItem value="rezept">💊 Rezept</MenuItem>
            <MenuItem value="befundung">🧪 Befundung</MenuItem>
          </Select>
        </Box>
      </Box>

      <Box sx={styles.tableWrapper}>
        <CustomDataGrid
          columns={columns}
          rows={rows}
          onRowClick={handleRowClick}
          getRowId={(row) => row.orderId}
          isCellEditable={() => false}
          isRowSelectable={() => false}
          pagination
          autoPageSize
          sx={styles.dataGrid}
          initialState={{
            sorting: {
              sortModel: [{ field: 'requestState', sort: 'asc' }],
            },
          }}
        />
      </Box>

      <CustomModal variant="large" isModalOpen={isEditModalOpen} onModalClose={() => setIsEditModalOpen(false)}>
        <RequestModal onModalClose={() => setIsEditModalOpen(false)} requestId={requestId} isFollowup={isFollowup} />
      </CustomModal>

      <CustomModal
        variant="large"
        isModalOpen={isBefundungModalOpen}
        onModalClose={() => setIsBefundungModalOpen(false)}
      >
        <BefundungModal
          onModalClose={() => setIsBefundungModalOpen(false)}
          requestId={requestId}
          isFollowup={isFollowup}
        />
      </CustomModal>
    </Box>
  );
};

export default OrderRequests;
